import { useEffect, useState } from "react";
import { onSnapshot, where, orderBy, limit } from "firebase/firestore";
import { orgQuery } from "@/lib/org";
import { db } from "@/lib/firebase";
import { useAuth } from "@/store/auth";
import { fmtDateTime, fmtYearMonth } from "@/lib/format";
import type { Delivery } from "@/types";
import { kstDay } from "@/lib/kst";

// 학년도(3월~다음 해 2월)를 오늘 날짜로 계산한다 — 코드에 박힌 달 목록은 내년에 틀린다(예행연습 02-24)
function seasonMonths(now = new Date()): string[] {
  const startYear = now.getMonth() + 1 >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(startYear, 2 + i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
}
const SEASON_MONTHS = seasonMonths();

export default function MyStats() {
  const { user } = useAuth();
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const today = new Date();
  const todayStr = kstDay(today);
  const thisMonth = fmtYearMonth();

  useEffect(() => {
    if (!user) return;
    const q = orgQuery(
      "deliveries",
      where("driverUid", "==", user.uid),
      orderBy("deliveredAt", "desc"),
      limit(1000),
    );
    const unsub = onSnapshot(q, (snap) => {
      setDeliveries(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
    });
    return () => unsub();
  }, [user?.uid]);

  const todayCount = deliveries.filter((d: any) => d.deliveredAt && kstDay(toDate(d.deliveredAt)) === todayStr).length;
  const monthCount = deliveries.filter((d) => d.yearMonth === thisMonth).length;
  const total = deliveries.length;

  const byMonth: Record<string, number> = {};
  for (const d of deliveries) byMonth[d.yearMonth] = (byMonth[d.yearMonth] || 0) + 1;

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold">📊 내 실적</h1>

      <div className="grid grid-cols-3 gap-2">
        <Stat label="오늘" value={todayCount} />
        <Stat label={`${thisMonth}`} value={monthCount} />
        <Stat label="누적(시즌)" value={total} />
      </div>

      <div className="card p-4">
        <h3 className="font-bold mb-2">이번 학년도 진행 ({SEASON_MONTHS[0].replace("-", ".")} ~ {SEASON_MONTHS[11].replace("-", ".")})</h3>
        <div className="space-y-1">
          {SEASON_MONTHS.map((m) => {
            const n = byMonth[m] || 0;
            const width = Math.min(100, (n / 200) * 100);
            return (
              <div key={m} className="flex items-center gap-2 text-sm">
                <div className="w-20 text-slate-500">{m}</div>
                <div className="flex-1 bg-slate-100 rounded h-4 overflow-hidden">
                  <div className="bg-milk-500 h-full" style={{ width: `${width}%` }} />
                </div>
                <div className="w-14 text-right font-bold">{n}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card p-4">
        <h3 className="font-bold mb-2">최근 배송</h3>
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="p-2">시각</th>
              <th className="p-2">학교</th>
              <th className="p-2">학생</th>
              <th className="p-2 text-center">사진</th>
            </tr>
          </thead>
          <tbody>
            {deliveries.slice(0, 50).map((d: any) => (
              <tr key={d.id} className="border-t">
                <td className="p-2 text-xs">{d.deliveredAt ? fmtDateTime(toDate(d.deliveredAt)) : "—"}</td>
                <td className="p-2">{d.school}</td>
                <td className="p-2 font-medium">{d.studentName}</td>
                <td className="p-2 text-center">
                  {d.photoUrl && <a className="text-milk-600 underline text-xs" href={d.photoUrl} target="_blank" rel="noreferrer">보기</a>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function toDate(ts: any): Date {
  if (ts?.toDate) return ts.toDate();
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  return new Date(ts);
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="card p-3 text-center">
      <div className="label">{label}</div>
      <div className="text-2xl font-extrabold text-milk-700 mt-1">{value.toLocaleString()}</div>
    </div>
  );
}
